import {RefreshCw} from 'lucide-react';
import {Tooltip} from '@/matic/components/ui/tooltip';

type SyncedFieldMarkerProps = {
  /** Human-readable source, e.g. "Cetec". */
  readonly sourceLabel: string;
  /** Why the field is locked, when the source is not an ERP sync (e.g. the quote engine). */
  readonly reason?: string;
};

/**
 * "from Cetec" marker for a field the ERP sync writes.
 *
 * The field it labels renders disabled, so per uxConventions §9.2 it carries
 * the reason: this is a *state* lock (the next sync run would overwrite the
 * edit), not a permission one. The value stays visible — hiding it would cost
 * the reader the data to spare them an input they can't use.
 */
export function SyncedFieldMarker({
  sourceLabel,
  reason,
}: SyncedFieldMarkerProps) {
  return (
    <Tooltip
      content={
        <span className="block max-w-[16rem]">
          {reason ?? (
            <>
              This field is fed by {sourceLabel}. An edit here wouldn&apos;t
              stick — the next sync would overwrite it. To take the field back,
              unmap it in the {sourceLabel} sync settings.
            </>
          )}
        </span>
      }
    >
      <span className="inline-flex items-center gap-1 rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600">
        <RefreshCw className="h-3 w-3" aria-hidden="true" />
        from {sourceLabel}
      </span>
    </Tooltip>
  );
}
